import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Video, Share2, CheckSquare, Info, Book, ChevronLeft } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import Loader from './Loader';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { useTheme } from '../contexts/ThemeContext';

const SharedLectureView = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [lecture, setLecture] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');

  useEffect(() => {
    let isMounted = true;

    const fetchLecture = async () => {
      if (!isLoaded || !isSignedIn || isLoading === false) return;
      
      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/share/${shareToken}/view`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch lecture');
        }

        const data = await response.json();
        if (isMounted) {
          setLecture(data);
          setClassInfo(data.class_info);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching lecture:', error);
          setError('Failed to load lecture');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchLecture();

    return () => {
      isMounted = false;
    };
  }, [shareToken, isLoaded, isSignedIn, isLoading]);

  const handleAddToAccount = async () => {
    if (!shareToken) return;
    
    setIsAdding(true);
    try {
      const response = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${shareToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ shareToken })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Lecture added to your account');
        navigate(`/class/${data.classId}/lecture/${data.id}`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add lecture');
      }
    } catch (error) {
      console.error('Error adding lecture:', error);
      toast.error('Failed to add lecture');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isLoaded) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!lecture) {
    return (
      <div className="p-4 text-gray-500">
        Lecture not found
      </div>
    );
  }

  const tabs = [
    { id: 'notes', label: 'Processed Notes', icon: <FileText size={20} /> },
    { id: 'transcript', label: 'Raw Transcript', icon: <Video size={20} /> }
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col" style={{ background: theme.background, color: theme.text }}>
      <header className="flex-shrink-0 border-b border-theme">
        <div className="h-full px-4 py-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm select-text">
            <button
              onClick={() => navigate('/classes')}
              className="text-accent hover:text-theme hover:underline flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Back to Classes
            </button>
          </nav>

          <div className="flex items-center justify-between mt-4">
            <div className="select-text">
              <h1 className="text-2xl font-bold text-theme">
                {lecture.title}
              </h1>
              <p className="text-sm text-accent">
                From {classInfo.title} • {classInfo.professor}
              </p>
              <p className="text-sm text-accent">
                {new Date(lecture.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
              {lecture.shared_by_name && (
                <p className="mt-2 text-sm flex items-center gap-2" style={{ color: theme.accent }}>
                  <Share2 size={14} />
                  Shared by {lecture.shared_by_name}
                </p>
              )}
            </div>
            <button
              onClick={handleAddToAccount}
              disabled={isAdding}
              className="px-4 py-2 rounded-lg flex items-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02]"
              style={{ 
                opacity: isAdding ? 0.5 : 1,
                cursor: isAdding ? 'not-allowed' : 'pointer'
              }}
            >
              <CheckSquare size={18} />
              {isAdding ? 'Adding...' : 'Add to My Account'}
            </button>
          </div>

          <div className="flex items-center space-x-4 mt-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 rounded-lg flex items-center gap-2 transition-all
                  ${activeTab === tab.id 
                    ? 'bg-primary text-theme' 
                    : 'bg-secondary text-accent hover:bg-primary-hover'}
                `}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <div className="p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                {activeTab === 'notes' ? (
                  <div className="prose max-w-none text-theme">
                    {lecture.processed_transcript?.content ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                      >
                        {lecture.processed_transcript.content}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-accent">
                        No processed notes available for this lecture.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-accent">
                    {lecture.transcript || (
                      <div className="text-accent">
                        No transcript available for this lecture.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                <div className="flex items-center gap-2 mb-4">
                  <Book size={20} className="text-accent" />
                  <h3 className="text-lg font-medium text-theme">Class Information</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-theme font-medium">{classInfo.title}</p>
                  <p className="text-accent">Professor: {classInfo.professor}</p>
                  <p className="text-accent">Location: {classInfo.location}</p>
                  <p className="text-accent">Credits: {classInfo.credits}</p>
                  {classInfo.description && (
                    <p className="text-accent mt-4">{classInfo.description}</p>
                  )}
                </div>
              </div>

              {lecture.processed_transcript?.summary && (
                <div className="p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                  <div className="flex items-center gap-2 mb-4">
                    <Info size={20} className="text-accent" />
                    <h3 className="text-lg font-medium text-theme">Summary</h3>
                  </div>
                  <p className="text-accent">
                    {lecture.processed_transcript.summary}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SharedLectureView; 